import React, { useEffect, useState } from 'react';
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  FormControl,
  IconButton,
  Radio,
  RadioGroup,
  TextField,
  Typography
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DosenButton from '../../../../../../../../components/ui/Buttons/DosenButton';
import { levelTypes } from '../../../../../../../../utils/constants';
import { convertToEpoch } from '../../../../../../../../utils/commonFunctions';
import moment from 'moment';

export interface DueDateData {
  startDate: { type: string; value: number };
  endDate: { type: string; value: number };
  description?: string;
}

export interface ProgramDueDateRule {
  type: string;
  value: number | null;
}

export interface ProgramDueDateData {
  // Members already enrolled as of firstConfiguredAt. "daysAfterSignup"
  // resolves to a shared date N days from today (computed server-side at
  // save time), since these members' real signup already happened.
  existingMembers: ProgramDueDateRule;
  // Members not yet enrolled as of firstConfiguredAt. "daysAfterSignup"
  // resolves per-member, N days after their own future signup.
  newMembers: ProgramDueDateRule;
}

interface DueDateModalProps {
  open: boolean;
  onClose: () => void;
  mode: 'module' | 'program';
  // Module mode
  initialData?: Partial<DueDateData>;
  onSave: (data: DueDateData) => void;
  // Program mode
  initialProgramData?: Partial<ProgramDueDateData>;
  onSaveProgram?: (data: ProgramDueDateData) => void;
}

const radioSx = { color: '#3A3A3A', '&.Mui-checked': { color: '#F79420' }, p: 0, mr: 1.5 };

// Applied via InputProps.sx so it wins over MUI's disabled-state overrides
const inputRootSx = {
  background: '#141414',
  borderRadius: '6px',
  fontSize: 14,
  color: '#FDFDFD',
  '&.Mui-disabled': { background: '#141414' },
  '& fieldset': { borderColor: '#2A2A2A' },
  '&:hover fieldset': { borderColor: '#565656' },
  '&.Mui-focused fieldset': { borderColor: '#F79420' }
};

const inputElementSx = {
  color: '#FDFDFD',
  WebkitTextFillColor: '#FDFDFD',
  '&.Mui-disabled, &:disabled': { WebkitTextFillColor: '#3A3A3A', color: '#3A3A3A' },
  '&::placeholder': { color: '#565656', opacity: 1 },
  py: '7px'
};

interface FieldProps {
  type: 'date' | 'number';
  disabled: boolean;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}

// Two-way segmented toggle, matching the dark-theme segmented control used
// for the Mentor/Learner switch on MemberProgramCard.tsx (no MUI/antd Tabs
// used elsewhere in this codebase).
const MemberBucketToggle: React.FC<{
  value: 'existing' | 'new';
  onChange: (next: 'existing' | 'new') => void;
}> = ({ value, onChange }) => (
  <Box
    role="group"
    aria-label="Existing or new people"
    sx={{ display: 'inline-flex', alignItems: 'center', borderRadius: '6px', overflow: 'hidden', mb: 1.5 }}
  >
    {(['existing', 'new'] as const).map((key) => (
      <Box
        key={key}
        component="button"
        type="button"
        onClick={() => onChange(key)}
        aria-pressed={value === key}
        sx={{
          px: 1.5,
          py: 0.75,
          bgcolor: value === key ? '#444444' : '#2A2A2A',
          color: value === key ? '#FDFDFD' : '#B6B6B6',
          fontFamily: 'Inter, sans-serif',
          fontSize: 13,
          fontWeight: value === key ? 600 : 400,
          lineHeight: 1.2,
          border: 'none',
          cursor: 'pointer',
          transition: 'background-color 0.12s, color 0.12s',
          '&:hover': { bgcolor: value === key ? '#4a4a4a' : '#333333' }
        }}
      >
        {key === 'existing' ? 'People Already Enrolled' : 'People Who Enroll Later'}
      </Box>
    ))}
  </Box>
);

const DateField: React.FC<FieldProps> = ({ type, disabled, value, onChange, placeholder }) => (
  <TextField
    type={type}
    fullWidth
    size="small"
    disabled={disabled}
    value={value}
    onChange={(e) => onChange(e.target.value)}
    placeholder={placeholder}
    InputProps={{
      sx: inputRootSx,
      inputProps: {
        min: type === 'number' ? 0 : undefined,
        style: { colorScheme: 'dark' }
      }
    }}
    sx={{
      mt: 0.75,
      '& input[type=number]': { MozAppearance: 'textfield' },
      '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': { WebkitAppearance: 'none' }
    }}
  />
);

const DueDateModal: React.FC<DueDateModalProps> = ({
  open,
  onClose,
  mode,
  initialData,
  onSave,
  initialProgramData,
  onSaveProgram
}) => {
  const isModule = mode === 'module';

  const [startType, setStartType] = useState<string>(levelTypes.activeNow);
  const [startValue, setStartValue] = useState<string>('');
  const [endType, setEndType] = useState<string>(levelTypes.activeNow);
  const [endValue, setEndValue] = useState<string>('');
  const [description, setDescription] = useState<string>('');

  const [memberBucket, setMemberBucket] = useState<'existing' | 'new'>('existing');
  const [existingType, setExistingType] = useState<string>(levelTypes.activeNow);
  const [existingValue, setExistingValue] = useState<string>('');
  const [newType, setNewType] = useState<string>(levelTypes.activeNow);
  const [newValue, setNewValue] = useState<string>('');

  // A picked date is saved as that day's UTC midnight (new Date('YYYY-MM-DD') is UTC), so it is
  // read back in UTC too: in local time, anywhere west of UTC it showed the day before.
  const dateInputValue = (epoch: number) => moment.unix(epoch).utc().format('YYYY-MM-DD');

  const resolveValue = (type: string, raw: string): number => {
    if (type === levelTypes.calendarDate) return convertToEpoch(new Date(raw));
    if (type === levelTypes.daysAfterSignup) return Number(raw);
    return 0;
  };

  const hydrateRule = (rule: { type: string; value: number | null } | undefined) => ({
    type: rule?.type || levelTypes.activeNow,
    value:
      rule?.type === levelTypes.calendarDate && rule.value
        ? dateInputValue(rule.value)
        : rule?.value
        ? String(rule.value)
        : ''
  });

  useEffect(() => {
    if (!open) return;
    if (isModule) {
      const sd = initialData?.startDate;
      const ed = initialData?.endDate;
      setStartType(sd?.type || levelTypes.activeNow);
      setStartValue(
        sd?.type === levelTypes.calendarDate
          ? dateInputValue(sd.value)
          : sd?.value ? String(sd.value) : ''
      );
      setEndType(ed?.type || levelTypes.activeNow);
      setEndValue(
        ed?.type === levelTypes.calendarDate
          ? dateInputValue(ed.value)
          : ed?.value ? String(ed.value) : ''
      );
      setDescription(initialData?.description || '');
    } else {
      const existing = hydrateRule(initialProgramData?.existingMembers);
      const newRule = hydrateRule(initialProgramData?.newMembers);
      setExistingType(existing.type);
      setExistingValue(existing.value);
      setNewType(newRule.type);
      setNewValue(newRule.value);
      setMemberBucket('existing');
    }
  }, [open]);

  const handleSave = () => {
    onSave({
      startDate: { type: startType, value: resolveValue(startType, startValue) },
      endDate: { type: endType, value: resolveValue(endType, endValue) },
      description
    });
    onClose();
  };

  const handleSaveProgram = () => {
    onSaveProgram?.({
      existingMembers: { type: existingType, value: resolveValue(existingType, existingValue) },
      newMembers: { type: newType, value: resolveValue(newType, newValue) }
    });
    onClose();
  };

  const optionLabel = (active: boolean, text: string) => (
    <Typography fontSize={14} color={active ? '#FDFDFD' : '#868686'} lineHeight="20px">
      {text}
    </Typography>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xs"
      disableScrollLock
      PaperProps={{
        sx: { bgcolor: '#1C1C1C', color: '#FDFDFD', borderRadius: 2, border: '1px solid #2A2A2A' }
      }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2.5, py: 1.5, borderBottom: '1px solid #2A2A2A' }}>
        <Typography fontWeight={600} fontSize={15} color="#FDFDFD">
          {isModule ? 'Add Module Due Date' : 'Add Program Due Date'}
        </Typography>
        <IconButton onClick={onClose} size="small" sx={{ color: '#565656', '&:hover': { color: '#FDFDFD' } }}>
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>

      <DialogContent sx={{ px: 2.5, py: 2, display: 'flex', flexDirection: 'column', gap: 2.5 }}>

        {/* ── Start Date (module only) ── */}
        {isModule && (
          <Box>
            <Typography fontWeight={600} fontSize={14} color="#FDFDFD" mb={0.5}>
              Select Start Date
            </Typography>
            <Typography fontSize={13} color="#868686" mb={1.5} lineHeight="18px">
              Display this module as 'Locked' until the chosen date.
            </Typography>

            <FormControl component="fieldset" fullWidth>
              <RadioGroup value={startType} onChange={(e) => { setStartType(e.target.value); setStartValue(''); }}>
                <Box display="flex" flexDirection="column" gap={1.5}>

                  <Box display="flex" alignItems="center">
                    <Radio value={levelTypes.activeNow} size="small" sx={radioSx} />
                    {optionLabel(startType === levelTypes.activeNow, 'Make module active now')}
                  </Box>

                  <Box display="flex" alignItems="flex-start">
                    <Radio value={levelTypes.calendarDate} size="small" sx={{ ...radioSx, mt: '1px' }} />
                    <Box flex={1}>
                      {optionLabel(startType === levelTypes.calendarDate, 'On a specific date')}
                      <DateField
                        type="date"
                        disabled={startType !== levelTypes.calendarDate}
                        value={startType === levelTypes.calendarDate ? startValue : ''}
                        onChange={setStartValue}
                      />
                    </Box>
                  </Box>

                  <Box display="flex" alignItems="flex-start">
                    <Radio value={levelTypes.daysAfterSignup} size="small" sx={{ ...radioSx, mt: '1px' }} />
                    <Box flex={1}>
                      {optionLabel(startType === levelTypes.daysAfterSignup, 'X days after the learner joins')}
                      <DateField
                        type="number"
                        disabled={startType !== levelTypes.daysAfterSignup}
                        value={startType === levelTypes.daysAfterSignup ? startValue : ''}
                        onChange={setStartValue}
                        placeholder="e.g. 30"
                      />
                    </Box>
                  </Box>

                  <Box display="flex" alignItems="flex-start">
                    <Radio value={levelTypes.availableSoon} size="small" sx={{ ...radioSx, mt: '1px' }} />
                    <Box flex={1}>
                      {optionLabel(startType === levelTypes.availableSoon, 'Available soon')}
                      <Typography fontSize={12} color="#565656" mt={0.25} lineHeight="16px">
                        Shows as "Available soon" until manually activated.
                      </Typography>
                    </Box>
                  </Box>

                </Box>
              </RadioGroup>
            </FormControl>

            {/* Module Description — part of Start Date section */}
            <Box mt={2}>
              <Typography fontSize={13} fontWeight={600} color="#868686" mb={0.25}>
                Module description
              </Typography>
              <Typography fontSize={12} color="#565656" mb={1} lineHeight="16px">
                Visible to people while the module is locked.
              </Typography>
              <TextField
                multiline
                minRows={2}
                fullWidth
                size="small"
                placeholder="Enter module description…"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                InputProps={{ sx: inputRootSx }}
                sx={{ '& textarea::placeholder': { color: '#565656', opacity: 1 } }}
              />
            </Box>
          </Box>
        )}

        {isModule && <Divider sx={{ borderColor: '#2A2A2A', my: -0.5 }} />}

        {/* ── Select Due Date (module) ── */}
        {isModule && (
          <Box>
            <Typography fontWeight={600} fontSize={14} color="#FDFDFD" mb={0.5}>
              Select Due Date
            </Typography>
            <Typography fontSize={13} color="#868686" mb={1.5} lineHeight="18px">
              People will see this due date on their dashboard.
            </Typography>

            <FormControl component="fieldset" fullWidth>
              <RadioGroup value={endType} onChange={(e) => { setEndType(e.target.value); setEndValue(''); }}>
                <Box display="flex" flexDirection="column" gap={1.5}>

                  <Box display="flex" alignItems="center">
                    <Radio value={levelTypes.activeNow} size="small" sx={radioSx} />
                    {optionLabel(endType === levelTypes.activeNow, 'No due date')}
                  </Box>

                  <Box display="flex" alignItems="flex-start">
                    <Radio value={levelTypes.calendarDate} size="small" sx={{ ...radioSx, mt: '1px' }} />
                    <Box flex={1}>
                      {optionLabel(endType === levelTypes.calendarDate, 'Due on a specific date')}
                      <DateField
                        type="date"
                        disabled={endType !== levelTypes.calendarDate}
                        value={endType === levelTypes.calendarDate ? endValue : ''}
                        onChange={setEndValue}
                      />
                    </Box>
                  </Box>

                  <Box display="flex" alignItems="flex-start">
                    <Radio value={levelTypes.daysAfterSignup} size="small" sx={{ ...radioSx, mt: '1px' }} />
                    <Box flex={1}>
                      {optionLabel(endType === levelTypes.daysAfterSignup, 'Due X days after the learner joins')}
                      <DateField
                        type="number"
                        disabled={endType !== levelTypes.daysAfterSignup}
                        value={endType === levelTypes.daysAfterSignup ? endValue : ''}
                        onChange={setEndValue}
                        placeholder="e.g. 30"
                      />
                    </Box>
                  </Box>

                </Box>
              </RadioGroup>
            </FormControl>
          </Box>
        )}

        {/* ── Select Due Date (program): existing vs. new members ── */}
        {!isModule && (
          <Box>
            <Typography fontWeight={600} fontSize={14} color="#FDFDFD" mb={0.5}>
              Select Due Date
            </Typography>
            <Typography fontSize={13} color="#868686" mb={1.5} lineHeight="18px">
              People will see this due date on their dashboard. Set independent rules for people already in the program
              and people who are not enrolled yet.
            </Typography>

            <MemberBucketToggle value={memberBucket} onChange={setMemberBucket} />

            {memberBucket === 'existing' ? (
              <FormControl component="fieldset" fullWidth>
                <RadioGroup value={existingType} onChange={(e) => { setExistingType(e.target.value); setExistingValue(''); }}>
                  <Box display="flex" flexDirection="column" gap={1.5}>

                    <Box display="flex" alignItems="center">
                      <Radio value={levelTypes.activeNow} size="small" sx={radioSx} />
                      {optionLabel(existingType === levelTypes.activeNow, 'No due date')}
                    </Box>

                    <Box display="flex" alignItems="flex-start">
                      <Radio value={levelTypes.calendarDate} size="small" sx={{ ...radioSx, mt: '1px' }} />
                      <Box flex={1}>
                        {optionLabel(existingType === levelTypes.calendarDate, 'Due on a specific date')}
                        <DateField
                          type="date"
                          disabled={existingType !== levelTypes.calendarDate}
                          value={existingType === levelTypes.calendarDate ? existingValue : ''}
                          onChange={setExistingValue}
                        />
                      </Box>
                    </Box>

                    <Box display="flex" alignItems="flex-start">
                      <Radio value={levelTypes.daysAfterSignup} size="small" sx={{ ...radioSx, mt: '1px' }} />
                      <Box flex={1}>
                        {optionLabel(existingType === levelTypes.daysAfterSignup, 'Due X days from today')}
                        <DateField
                          type="number"
                          disabled={existingType !== levelTypes.daysAfterSignup}
                          value={existingType === levelTypes.daysAfterSignup ? existingValue : ''}
                          onChange={setExistingValue}
                          placeholder="e.g. 30"
                        />
                        <Typography fontSize={12} color="#565656" mt={0.5} lineHeight="16px">
                          Calculated from today's date, since these people have already signed up.
                        </Typography>
                      </Box>
                    </Box>

                  </Box>
                </RadioGroup>
              </FormControl>
            ) : (
              <FormControl component="fieldset" fullWidth>
                <RadioGroup value={newType} onChange={(e) => { setNewType(e.target.value); setNewValue(''); }}>
                  <Box display="flex" flexDirection="column" gap={1.5}>

                    <Box display="flex" alignItems="center">
                      <Radio value={levelTypes.activeNow} size="small" sx={radioSx} />
                      {optionLabel(newType === levelTypes.activeNow, 'No due date')}
                    </Box>

                    <Box display="flex" alignItems="flex-start">
                      <Radio value={levelTypes.calendarDate} size="small" sx={{ ...radioSx, mt: '1px' }} />
                      <Box flex={1}>
                        {optionLabel(newType === levelTypes.calendarDate, 'Due on a specific date')}
                        <DateField
                          type="date"
                          disabled={newType !== levelTypes.calendarDate}
                          value={newType === levelTypes.calendarDate ? newValue : ''}
                          onChange={setNewValue}
                        />
                      </Box>
                    </Box>

                    <Box display="flex" alignItems="flex-start">
                      <Radio value={levelTypes.daysAfterSignup} size="small" sx={{ ...radioSx, mt: '1px' }} />
                      <Box flex={1}>
                        {optionLabel(newType === levelTypes.daysAfterSignup, 'Due X days after the learner joins')}
                        <DateField
                          type="number"
                          disabled={newType !== levelTypes.daysAfterSignup}
                          value={newType === levelTypes.daysAfterSignup ? newValue : ''}
                          onChange={setNewValue}
                          placeholder="e.g. 30"
                        />
                        <Typography fontSize={12} color="#565656" mt={0.5} lineHeight="16px">
                          Calculated from each person's own signup date, once they join.
                        </Typography>
                      </Box>
                    </Box>

                  </Box>
                </RadioGroup>
              </FormControl>
            )}
          </Box>
        )}

      </DialogContent>

      <DialogActions sx={{ px: 2.5, py: 1.5, borderTop: '1px solid #2A2A2A', gap: 1 }}>
        <DosenButton tone="basic" sizeVariant="sm" onClick={onClose}>Cancel</DosenButton>
        <DosenButton tone="cta" sizeVariant="sm" onClick={isModule ? handleSave : handleSaveProgram}>Save</DosenButton>
      </DialogActions>
    </Dialog>
  );
};

export default DueDateModal;
